import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useApolloContext, useAuthContext } from 'contexts';
import { apps, routeCheckers } from '../single-spa-config';
import { DownloadMobileAppModal } from 'components/DownloadMobileAppModal';
import { usePresenceNotifier } from 'hooks/usePresenceNotifier';
import { NavSidebar } from 'components/NavSidebar/NavSidebar';
import { MicroFrontendErrorBoundary } from 'components/MicroFrontendErrorBoundary';
import { Layout } from '@robinpowered/ui-kit';

export const AppShell = (): JSX.Element => {
  const location = useLocation();
  const { tenantId } = useApolloContext();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { setOrgSlug } = useAuthContext();

  useEffect(() => {
    setOrgSlug(orgSlug);
  }, [orgSlug, setOrgSlug]);

  // @TODO: Should this be a prop passed from the child app up to the parent?
  const isFullScreen = React.useMemo(() => {
    // When embedded in an iframe (e.g. schedule app embedding tickets), hide the nav sidebar.
    // NOTE: this assumes that all embedded apps are full-screen. We will need a more
    // detailed check in the future if embedded apps need to show the nav sidebar.
    if (window.self !== window.top) {
      return true;
    }

    const isApplicationFullScreen = apps.find((app) =>
      app.activeWhen(window.location)
    )?.isFullScreen;
    const isRouteFullScreen = routeCheckers.some((checker) =>
      checker(location.pathname)
    );

    return isApplicationFullScreen || isRouteFullScreen;
  }, [location.pathname]);

  usePresenceNotifier({ activeOrgId: tenantId ?? null });

  return (
    <Container>
      <DownloadMobileAppModal />

      {!isFullScreen && <NavSidebar />}

      <MainContainer>
        <ContentWrapper>
          <MicroFrontendErrorBoundary>
            <SingleSpaRoot id="singleSpaRoot" />
          </MicroFrontendErrorBoundary>
        </ContentWrapper>
      </MainContainer>
    </Container>
  );
};

const Container = styled(Layout)`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: row;
  height: 100vh;
`;

const { Content } = Layout;
const MainContainer = styled(Content)`
  background: #f8f8f8;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
  height: 100%;
  position: relative;
`;

const SingleSpaRoot = styled.div`
  height: 100%;
`;
